import { EntityVersionsPermission } from '@idc-forms/permissions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import {
  entityVersionsService,
  type VersionedEntityKind
} from '../services/entity-versions.service'

/**
 * El permiso es POR DOMINIO, no uno solo para los dos.
 *
 * El historial de un cliente conserva KYC —documento de identidad, patrimonio, y los datos que
 * alguien corrigió PORQUE no correspondía que estuvieran—; el de una negociación es información
 * comercial. Que alguien pueda auditar cambios comerciales no implica que deba leer el
 * patrimonio viejo de una persona, así que se conceden aparte.
 */
const VERSION_PERMISSIONS = {
  client: {
    read: EntityVersionsPermission.ReadClientVersions,
    restore: EntityVersionsPermission.RestoreClientVersion
  },
  negotiation: {
    read: EntityVersionsPermission.ReadNegotiationVersions,
    restore: EntityVersionsPermission.RestoreNegotiationVersion
  }
} as const satisfies Record<VersionedEntityKind, { read: string; restore: string }>

/**
 * Historial de versiones de un expediente (ADR 0029, Fase 2).
 *
 * ── POR QUÉ EL PERMISO SE MIRA ACÁ Y NO SÓLO EN EL SERVIDOR ──────────────────
 *
 * El portón real es del servidor —el permiso de lectura del dominio, más el alcance del
 * expediente— y esto NO lo reemplaza. Sirve para no PEDIR lo que se sabe que va a dar 403: sin
 * el chequeo, cada apertura de una ficha dispararía una llamada condenada y un toast de error
 * para la mayoría de los roles, que es exactamente el defecto que tenía el filtro de asesores.
 *
 * Los permisos nacen sin asignar a ningún rol, así que hoy `enabled` es falso para casi todos.
 */
export const useEntityVersionsQuery = (kind: VersionedEntityKind, id: string | null) => {
  const { auth } = useAuth()
  const { has } = usePermissions()
  const accessToken = auth?.v2AccessToken ?? ''
  const canRead = has(VERSION_PERMISSIONS[kind].read)

  return useQuery({
    queryKey: ['entity-versions', kind, id] as const,
    queryFn: async () => await entityVersionsService.timeline(accessToken, kind, id ?? ''),
    enabled: Boolean(accessToken && id && canRead),
    // El historial sólo crece al editar, y quien está mirándolo no está editando.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false
  })
}

/**
 * Una versión concreta con su contenido y su diff.
 *
 * Se pide SÓLO cuando alguien abre una entrada del timeline: el snapshot es lo pesado del
 * historial, y traerlo para todas al pintar la lista sería un megabyte para mostrar fechas.
 */
export const useEntityVersionDetailQuery = (
  kind: VersionedEntityKind,
  id: string | null,
  version: number | null
) => {
  const { auth } = useAuth()
  const { has } = usePermissions()
  const accessToken = auth?.v2AccessToken ?? ''
  const canRead = has(VERSION_PERMISSIONS[kind].read)

  return useQuery({
    queryKey: ['entity-versions', kind, id, version] as const,
    queryFn: async () =>
      await entityVersionsService.detail(accessToken, kind, id ?? '', version ?? 0),
    enabled: Boolean(accessToken && id && version && canRead),
    // Una versión es INMUTABLE: la colección es append-only, así que una vez traída no cambia
    // nunca. No tiene sentido revalidarla.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false
  })
}

/** Si la persona puede ver el historial de ESE dominio. Decide si la pestaña se ofrece. */
export const useCanReadEntityVersions = (kind: VersionedEntityKind): boolean => {
  const { has } = usePermissions()
  return has(VERSION_PERMISSIONS[kind].read)
}

/** Si la persona puede restaurar. Es un permiso APARTE de poder editar el expediente. */
export const useCanRestoreEntityVersion = (kind: VersionedEntityKind): boolean => {
  const { has } = usePermissions()
  return has(VERSION_PERMISSIONS[kind].restore)
}

/**
 * Restaura el expediente a una versión anterior.
 *
 * Invalida TODO lo que cuelga del expediente y no sólo el historial: la restauración reescribe
 * el documento vivo, así que el detalle, el listado y las pestañas abiertas quedan mostrando
 * datos que ya no existen. Es la clase de refresco donde ser conservador cuesta una consulta y
 * ser fino cuesta que alguien lea una pantalla vieja y vuelva a guardarla.
 */
export const useRestoreEntityVersion = (kind: VersionedEntityKind, id: string | null) => {
  const { auth } = useAuth()
  const queryClient = useQueryClient()
  const accessToken = auth?.v2AccessToken ?? ''

  return useMutation({
    mutationFn: async (version: number) => {
      if (!id) throw new Error('No hay expediente que restaurar.')
      await entityVersionsService.restore(accessToken, kind, id, version)
    },
    onSuccess: async (_data, version) => {
      toast.success(`Expediente restaurado a la versión ${version}`)
      await queryClient.invalidateQueries({ queryKey: ['entity-versions', kind, id] })
      await queryClient.invalidateQueries({
        queryKey: kind === 'client' ? queryKeys.clients.all : queryKeys.negotiations.all
      })
    },
    onError: (error) => handleMutationError(error)
  })
}
